import React from "react";
import { Banner, Button, Dialog } from "@/ds";
import { ApiError, cockpit, FILED_MESSAGE_TRASH_CONFLICT } from "@/api";
import { cachedRead } from "@/api/read-cache";
import type {
  FiledMessageDetail, FiledMessageScope, FiledMessageSummary, FiledReason, FiledTrashRefusal,
} from "@/api/types";
import { fmtDate, Icon } from "@/lib";
import type { PushToast } from "@/notifications/toast-queue";
import { useT } from "../i18n";
import { useSession } from "../session";
import { ShellRouteLink } from "../shell-sidebar";
import { OverviewHeadTools, type OverviewHeadList } from "./overview-head-tools";
import { useIncomingWorkRead } from "./overview-incoming-work";
import styles from "./screen-filed.module.css";

interface FiledCounts {
  open: number;
  total: number;
  trash: number;
  trashRetentionDays: number;
}

/** #1536: the list this scope showed last time, while it is still remembered. */
function rememberedFiledResult(scope: FiledMessageScope): FiledResultState | null {
  const page = cachedRead("listFiledMessages", { scope });
  return page ? { status: "ready", rows: page.items, counts: countsOf(page) } : null;
}

function countsOf(page: { open: number; total: number; trash?: number; trashRetentionDays?: number }): FiledCounts {
  return { open: page.open, total: page.total, trash: page.trash ?? 0, trashRetentionDays: page.trashRetentionDays ?? 30 };
}

const FILED_TRASH_REFUSALS: readonly FiledTrashRefusal[] = ["already_in_trash", "not_in_trash", "promoted", "purge_due"];

/** #2433: the reason the server refused a trash move, restore or promotion, else null. */
function filedTrashRefusal(error: unknown): FiledTrashRefusal | null {
  if (!(error instanceof ApiError) || error.status !== 409) return null;
  const data = error.data as Record<string, unknown> | null;
  if (data?.code !== FILED_MESSAGE_TRASH_CONFLICT) return null;
  return FILED_TRASH_REFUSALS.find((known) => known === data.reason) ?? null;
}

type FiledResultState =
  | { status: "loading" }
  | { status: "error" }
  | {
      status: "ready";
      rows: FiledMessageSummary[];
      counts: FiledCounts;
    };

/**
 * What the pipeline set aside (#794).
 *
 * A first-class screen rather than a hidden table, because the question it answers is "where did
 * that enquiry go" — and an operator who cannot answer it stops trusting the inbox. Every row is
 * openable, says why it was filed, and can be turned back into a request in one click.
 */
export function FiledScreen(props: {
  onOpenRequest: (id: string) => void;
  pushToast: PushToast;
  /** #2249: the way back to the Overview this page is opened from. */
  overview: { href: string; onOpen: () => void };
  /** #2259: the Overview's head tools; a list switch opens the Overview on that list. */
  lists: { filed: { href: string; count: number; onOpen: () => void }; onSelect: (list: OverviewHeadList) => void };
}): React.ReactElement {
  const t = useT();
  const { user } = useSession();
  const incoming = useIncomingWorkRead();
  // #2259: the filter the head's filter switch shows and hides. It starts open, as before.
  const [filtersExpanded, setFiltersExpanded] = React.useState(true);
  const [scope, setScope] = React.useState<FiledMessageScope>("open");
  const [result, setResult] = React.useState<FiledResultState>(() => rememberedFiledResult("open") ?? { status: "loading" });
  const [openId, setOpenId] = React.useState<string | null>(null);
  const [detail, setDetail] = React.useState<FiledMessageDetail | null>(null);
  const [detailError, setDetailError] = React.useState(false);
  const [detailAttempt, setDetailAttempt] = React.useState(0);
  const [busyId, setBusyId] = React.useState<string | null>(null);
  // #2433: the item whose move to the trash waits for confirmation.
  const [trashTarget, setTrashTarget] = React.useState<FiledMessageSummary | null>(null);
  const currentScope = React.useRef(scope);
  const listRequest = React.useRef(0);
  const mounted = React.useRef(false);
  const openingAttempt = React.useRef<FiledMessageDetail | null>(null);

  const load = React.useCallback(async () => {
    if (!mounted.current) return;
    const request = ++listRequest.current;
    setResult(rememberedFiledResult(currentScope.current) ?? { status: "loading" });
    try {
      const page = await cockpit.listFiledMessages({ scope: currentScope.current });
      if (request !== listRequest.current) return;
      setResult({
        status: "ready",
        rows: page.items,
        counts: countsOf(page),
      });
    } catch {
      if (request === listRequest.current) setResult({ status: "error" });
    }
  }, []);

  React.useEffect(() => {
    mounted.current = true;
    void load();
    return () => {
      mounted.current = false;
      listRequest.current += 1;
    };
  }, [load]);

  function selectScope(next: FiledMessageScope): void {
    if (currentScope.current === next) return;
    // Invalidate the previous read in the interaction, including batched switches back to a scope.
    currentScope.current = next;
    setScope(next);
    void load();
  }

  React.useEffect(() => {
    if (!openId) {
      setDetail(null);
      setDetailError(false);
      return;
    }
    let live = true;
    setDetail(null);
    setDetailError(false);
    void cockpit
      .getFiledMessage(openId)
      .then((value) => {
        if (live) setDetail(value);
      })
      .catch(() => {
        if (live) setDetailError(true);
      });
    return () => {
      live = false;
    };
  }, [detailAttempt, openId]);

  // A loaded list or hidden tab is not an opening. Record only the detail actually on screen;
  // the server owns team eligibility and the first-writer check, as for request workspaces.
  React.useEffect(() => {
    if (!detail || detail.id !== openId || detail.firstOpenedAt
      || result.status !== "ready" || !result.rows.some((row) => row.id === openId && !row.trashedAt)
      || user?.tenantStatus !== "active" || (user.role !== "admin" && user.role !== "operator")) return;
    let active = true;
    const record = () => {
      if (document.visibilityState !== "visible" || openingAttempt.current === detail) return;
      openingAttempt.current = detail;
      void cockpit.recordFiledMessageOpening(detail.id).catch(() => {
        if (openingAttempt.current === detail) openingAttempt.current = null;
        if (active) props.pushToast({ tone: "danger", title: t("filed.openingFailed") });
      });
    };
    record();
    document.addEventListener("visibilitychange", record);
    return () => {
      active = false;
      document.removeEventListener("visibilitychange", record);
    };
  }, [detail, openId, result, user?.role, user?.tenantStatus, props.pushToast, t]);

  async function promote(id: string): Promise<void> {
    setBusyId(id);
    try {
      const result = await cockpit.promoteFiledMessage(id);
      props.pushToast({
        tone: "success",
        title: t(result.alreadyPromoted ? "filed.alreadyPromoted" : "filed.promoted"),
      });
      await load();
      props.onOpenRequest(result.requestId);
    } catch (error) {
      const refusal = filedTrashRefusal(error);
      props.pushToast({ tone: "danger", title: t(refusal ? `filed.trash.refused.${refusal}` : "filed.promoteFailed") });
      if (refusal) await load();
    } finally {
      setBusyId(null);
    }
  }

  /** #2433: move to the trash or restore; the server decides, and a refusal says why. */
  async function changeTrash(row: FiledMessageSummary, action: "trash" | "restore"): Promise<void> {
    setBusyId(row.id);
    try {
      if (action === "trash") await cockpit.trashFiledMessage(row.id);
      else await cockpit.restoreFiledMessage(row.id);
      setTrashTarget(null);
      if (openId === row.id) setOpenId(null);
      props.pushToast({ tone: "success", title: t(action === "trash" ? "filed.trash.movedToast" : "filed.trash.restoredToast") });
      await load();
    } catch (error) {
      const refusal = filedTrashRefusal(error);
      setTrashTarget(null);
      props.pushToast({
        tone: "danger",
        title: t(refusal ? `filed.trash.refused.${refusal}` : action === "trash" ? "filed.trash.failed" : "filed.trash.restoreFailed"),
      });
      if (refusal) await load();
    } finally {
      setBusyId(null);
    }
  }

  const reasonLabel = (reason: FiledReason): string => t(`filed.reason.${reason}`);
  const resultStatus = result.status !== "ready"
    ? result.status
    : result.rows.length === 0 ? "empty" : "populated";

  return (
    <div className="screen">
      <header className={`screen__header ${styles.heading}`}>
        <ShellRouteLink href={props.overview.href} onNavigate={props.overview.onOpen}
          className={`neo-btn neo-btn--ghost neo-btn--sm ${styles.back}`} data-testid="filed-back">
          <Icon name="arrowLeft" size={16} />
          {t("ws.backToInbox")}
        </ShellRouteLink>
        <div className={`ov-head ${styles.head}`}>
          <div className="ov-heading">
            <h1 className="ov-title">{t("filed.title")}</h1>
          </div>
          <OverviewHeadTools
            incoming={incoming}
            filter={{
              expanded: filtersExpanded,
              controls: "filed-filters",
              testId: "filed-filter-toggle",
              onToggle: () => setFiltersExpanded((current) => !current),
            }}
            current="filed"
            onSelectList={props.lists.onSelect}
            filed={props.lists.filed}
          />
        </div>
        <p className="subtle">{t("filed.intro")}</p>
      </header>

      <div className={styles.toolbar}>
        <div
          id="filed-filters"
          role="group"
          aria-label={t("filed.filtersAria")}
          className={styles.filters}
          hidden={!filtersExpanded}
        >
          <Button
            variant={scope === "open" ? "primary" : "secondary"}
            aria-pressed={scope === "open"}
            aria-controls="filed-results"
            onClick={() => selectScope("open")}
          >
            {t("filed.scope.open")}
          </Button>
          <Button
            variant={scope === "all" ? "primary" : "secondary"}
            aria-pressed={scope === "all"}
            aria-controls="filed-results"
            onClick={() => selectScope("all")}
          >
            {t("filed.scope.all")}
          </Button>
          <Button
            variant={scope === "trash" ? "primary" : "secondary"}
            aria-pressed={scope === "trash"}
            aria-controls="filed-results"
            iconLeft={<Icon name="trash" size={15} />}
            onClick={() => selectScope("trash")}
            data-testid="filed-scope-trash"
          >
            {t("filed.scope.trash")}
          </Button>
        </div>
        {result.status === "ready" ? (
          <p className={styles.count} data-testid="filed-count">
            {scope === "trash"
              ? t("filed.trash.count", {
                n: String(result.counts.trash),
                days: String(result.counts.trashRetentionDays),
              })
              : t("filed.count", {
                open: String(result.counts.open),
                total: String(result.counts.total),
              })}
          </p>
        ) : null}
      </div>

      <div
        id="filed-results"
        role="region"
        aria-label={t("filed.resultsAria")}
        aria-busy={result.status === "loading"}
        data-state={resultStatus}
        data-testid="filed-results"
      >
        {result.status === "loading" ? (
          <p className="subtle" role="status" data-testid="filed-loading">
            {t("filed.loading")}
          </p>
        ) : null}
        {result.status === "error" ? (
          <Banner
            tone="danger"
            title={t("filed.loadFailed")}
            actions={(
              <Button variant="secondary" size="sm" onClick={() => { void load(); }}>
                {t("common.retry")}
              </Button>
            )}
            data-testid="filed-error"
          />
        ) : null}
        {result.status === "ready" && result.rows.length === 0 ? (
          <p className="subtle" data-testid="filed-empty">
            {t(scope === "trash" ? "filed.trash.empty" : "filed.empty")}
          </p>
        ) : null}

        {result.status === "ready" && result.rows.length > 0 ? (
          <ul className={styles.list}>
            {result.rows.map((row) => (
              <li key={row.id} data-testid="filed-row" className={styles.row}>
                <div className={styles.rowMain}>
                  <div className={styles.content}>
                    <h2 className={styles.subject}>{row.subject ?? t("filed.noSubject")}</h2>
                    <p className={styles.sender}>{row.fromAddr ?? ""}</p>
                    <div className={styles.metadata}>
                      <span data-testid="filed-reason">{reasonLabel(row.filedReason)}</span>
                      {row.promotedAt ? (
                        <span className={styles.promoted}>{t("filed.promotedNote")}</span>
                      ) : null}
                      {row.purgeAt ? (
                        <span data-testid="filed-purge-at">
                          {t("filed.trash.purgeAt", { date: fmtDate(row.purgeAt) })}
                        </span>
                      ) : null}
                    </div>
                  </div>
                  <div className={styles.actions}>
                    <Button
                      variant="secondary"
                      aria-expanded={openId === row.id}
                      aria-controls={openId === row.id ? `filed-detail-${row.id}` : undefined}
                      onClick={() => setOpenId(openId === row.id ? null : row.id)}
                    >
                      {openId === row.id ? t("filed.close") : t("filed.open")}
                    </Button>
                    {/* Whether it was handled is `promotedAt`, not the request id: the request can be
                        deleted afterwards (the trash purge does), and keying off the id would offer to
                        promote a message that already was — a second request for the same mail. */}
                    {row.trashedAt ? (
                      <Button
                        variant="secondary"
                        disabled={busyId === row.id}
                        aria-busy={busyId === row.id}
                        onClick={() => void changeTrash(row, "restore")}
                        data-testid="filed-restore"
                      >
                        {t("filed.trash.restore")}
                      </Button>
                    ) : !row.promotedAt ? (
                      <>
                        <Button
                          variant="ghost"
                          disabled={busyId === row.id}
                          aria-busy={busyId === row.id}
                          onClick={() => void promote(row.id)}
                        >
                          {t("filed.promote")}
                        </Button>
                        {/* #2433: only mail still set aside; a promoted item belongs to its request. */}
                        <Button
                          variant="danger-quiet"
                          disabled={busyId === row.id}
                          iconLeft={<Icon name="trash" size={15} />}
                          onClick={() => setTrashTarget(row)}
                          data-testid="filed-trash"
                        >
                          {t("filed.trash.move")}
                        </Button>
                      </>
                    ) : row.promotedRequestId ? (
                      <Button
                        variant="secondary"
                        onClick={() => props.onOpenRequest(row.promotedRequestId)}
                      >
                        {t("filed.openRequest")}
                      </Button>
                    ) : null}
                  </div>
                </div>
                {openId === row.id ? (
                  <div id={`filed-detail-${row.id}`} className={styles.detail}>
                    {detail && detail.id === openId ? (
                      <div data-testid="filed-detail">
                        <div className={styles.evidence}>
                          <h3>{t("filed.whyTitle")}</h3>
                          <p className={styles.explanation} data-testid="filed-explanation">
                            {t(`filed.explanation.${detail.filedReason}`)}
                          </p>
                          {detail.filedReason === "model_non_enquiry" ? (
                            <div>
                              {detail.filedSignals.map((signal, index) => signal.header === "category" ? (
                                <p key={index}>{t(`filed.category.${signal.value}`)}</p>
                              ) : (
                                <div key={index}>
                                  <strong>{t(`filed.evidence.${signal.header}`)}</strong>
                                  <blockquote>{signal.value}</blockquote>
                                </div>
                              ))}
                            </div>
                          ) : <details className={styles.technical}>
                            <summary>{t("filed.technicalDetails")}</summary>
                            {detail.filedSignals.length > 0 ? (
                              <ul>
                                {detail.filedSignals.map((signal, index) => (
                                  <li key={`${signal.header}-${index}`}>
                                    <code>{signal.header}: {signal.value}</code>
                                  </li>
                                ))}
                              </ul>
                            ) : <p className={styles.explanation}>{t("filed.noSignals")}</p>}
                          </details>}
                        </div>
                        <pre className={styles.message}>{detail.normalizedText ?? ""}</pre>
                      </div>
                    ) : detailError ? (
                      <Banner
                        tone="danger"
                        title={t("filed.loadFailed")}
                        actions={(
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => setDetailAttempt((attempt) => attempt + 1)}
                          >
                            {t("common.retry")}
                          </Button>
                        )}
                        data-testid="filed-detail-error"
                      />
                    ) : (
                      <p className={styles.count} role="status">{t("filed.loading")}</p>
                    )}
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        ) : null}
      </div>

      <Dialog
        open={trashTarget !== null}
        onClose={busyId !== null ? undefined : () => setTrashTarget(null)}
        tone="danger"
        icon={<Icon name="trash" size={20} />}
        title={t("filed.trash.confirmTitle")}
        footer={<>
          <Button variant="ghost" disabled={busyId !== null} onClick={() => setTrashTarget(null)}>
            {t("common.cancel")}
          </Button>
          <Button
            variant="destructive"
            loading={trashTarget !== null && busyId === trashTarget.id}
            disabled={busyId !== null}
            onClick={() => { if (trashTarget) void changeTrash(trashTarget, "trash"); }}
            data-testid="filed-trash-confirm"
          >
            {t("filed.trash.move")}
          </Button>
        </>}
      >
        <p style={{ margin: 0, paddingBottom: "var(--space-4)" }}>
          {t("filed.trash.confirmBody", {
            days: String(result.status === "ready" ? result.counts.trashRetentionDays : 30),
          })}
        </p>
      </Dialog>
    </div>
  );
}
